import HomepageSEO from '@components/common/SEO/HomepageSEO';
import HeaderWithCTA from '@components/header-ai';
import TrendsFamilyListing from '@components/trends/TrendsFamilyListing';
import VideoWallCategoryTemplateHead from '@components/video-wall-category-templates/VideoWallCategoryTemplateHead';
import { footerExploreLinks } from '@constants/footerExplore';
import Footer from '@invideoio/web-shared/components/Footer';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import {
  PAGE_TYPE,
  Referrer,
} from '@invideoio/web-shared/utils/utils.constant';
import AppLayout from '@layouts/AppLayout';
import { TrendGroup } from '@lib/ProAPI/pro-api.types';
import { getTrends } from '@lib/ProAPI/pro.api';
import { StateContext } from '@store/store';
import { AmplitudeEvents, sendAmplitudeAnalyticEvent } from '@utils/analytics';
import { setCacheControlHeaders } from '@utils/server.utils';
import { GetServerSidePropsContext } from 'next';
import useTranslation from 'next-translate/useTranslation';
import React, { useContext, useEffect } from 'react';

function TrendPage({ trend, pageData }: { trend: TrendGroup; pageData: any }) {
  const globalState = useContext(StateContext);
  const { t, lang } = useTranslation('home');
  const { platform, os, config, redirectionLink } = globalState;

  useEffect(() => {
    const AppName = process.env.NEXT_PUBLIC_APP_NAME;

    if (AppName === 'kizoa') {
      AnalyticsUtils.setReferrer(Referrer.KIZOA);
    } else {
      AnalyticsUtils.setReferrer(Referrer.WEBSITE);
    }

    AnalyticsUtils.registerPage('Trends Page' as any, {
      location: window.location.href,
    });

    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad);
  }, []);

  return (
    <AppLayout platform={platform}>
      <HomepageSEO seo={pageData.SEO} />
      <HeaderWithCTA isCTAIntersected={true} />

      <VideoWallCategoryTemplateHead Head={pageData.Head} />

      <TrendsFamilyListing trendsFamily={trend.trends_families} />

      <Footer
        os={os}
        platform={platform}
        socialLinks={globalState.socialLinks}
        exploreLinks={footerExploreLinks[config?.env?.siteId][lang].links}
      />
    </AppLayout>
  );
}

export default TrendPage;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const query = context.query;

  if (typeof query.trend !== 'string') {
    return {};
  }
  let maxage = 3600;

  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    maxage = 300; // 5mins
  }

  setCacheControlHeaders(context, maxage);

  const trendsResponse = await getTrends();

  const trend = trendsResponse.trends.find(trend => trend.id === query.trend);
  if (!trend) {
    return { notFound: true };
  }

  const pageData = {
    SEO: {
      Title: trend.display_name
        ? `${trend.display_name} - invideo AI`
        : 'Made with love, using Invideo',
      Description: trend.description || '',
      CanonicalURL: `https://invideo.io/trends/${trend.id}/`,
      Hreflang: [],
    },
    Head: {
      Title: trend.display_name || 'Made with love, using Invideo',
      HighlightedTitle: '',
      SubTitle: trend.description || '',
    },
  };

  return {
    props: {
      trend,
      pageData,
    },
  };
}
